import { useEffect, useRef, useCallback, useState } from 'react';

interface ParticleWorkerConfig {
  particleCount?: number;
  maxFrameRate?: number;
  enablePhysics?: boolean;
  gravity?: number;
  windForce?: number;
  boundaryBehavior?: 'wrap' | 'bounce' | 'respawn';
  colors?: string[];
  particleSize?: { min: number; max: number };
  velocity?: { min: number; max: number };
  lifetime?: { min: number; max: number };
  canvas?: {
    width: number;
    height: number;
    devicePixelRatio: number;
  };
}

interface ParticleData {
  id: string;
  x: number;
  y: number;
  size: number;
  color: string;
  opacity: number;
  needsUpdate: boolean;
}

interface PerformanceMetrics {
  averageFrameTime: number;
  droppedFrames: number;
  particleCount: number;
  lastUpdate: number;
}

interface UseParticleWorkerOptions {
  autoStart?: boolean;
  performanceMode?: 'high' | 'medium' | 'low';
  enablePerformanceMonitoring?: boolean;
  onPerformanceModeChange?: (mode: string, reason: string) => void;
  onFrameUpdate?: (particles: ParticleData[], metrics?: PerformanceMetrics) => void;
}

export const useParticleWorker = (options: UseParticleWorkerOptions = {}) => {
  const {
    autoStart = true,
    performanceMode = 'high',
    enablePerformanceMonitoring = true,
    onPerformanceModeChange,
    onFrameUpdate
  } = options;

  const workerRef = useRef<Worker | null>(null);
  const [isWorkerReady, setIsWorkerReady] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [currentPerformanceMode, setCurrentPerformanceMode] = useState(performanceMode);
  const [performanceMetrics, setPerformanceMetrics] = useState<PerformanceMetrics | null>(null);
  const configRef = useRef<ParticleWorkerConfig>({});

  // Initialize worker
  useEffect(() => {
    try {
      const worker = new Worker('/workers/particle-animation-worker.js');
      workerRef.current = worker;

      // Set up message handlers
      worker.onmessage = (event) => {
        const { type, data } = event.data;

        switch (type) {
          case 'WORKER_READY':
            console.log('[Particle Hook] Worker ready, version:', data?.version);
            setIsWorkerReady(true);
            break;

          case 'FRAME_UPDATE':
            if (data && onFrameUpdate) {
              onFrameUpdate(data.particles, data.performanceMetrics);
            }
            if (data?.performanceMetrics) {
              setPerformanceMetrics(data.performanceMetrics);
            }
            break;

          case 'PERFORMANCE_MODE_CHANGED':
            console.log('[Particle Hook] Performance mode changed:', data.mode, '-', data.reason);
            setCurrentPerformanceMode(data.mode);
            if (onPerformanceModeChange) {
              onPerformanceModeChange(data.mode, data.reason);
            }
            break;

          case 'PERFORMANCE_METRICS':
            setPerformanceMetrics(data);
            break;

          default:
            console.warn('[Particle Hook] Unknown message type:', type);
        }
      };

      worker.onerror = (error) => {
        console.error('[Particle Hook] Worker error:', error);
      };

      return () => {
        if (workerRef.current) {
          workerRef.current.terminate();
          workerRef.current = null;
          setIsWorkerReady(false);
          setIsRunning(false);
        }
      };
    } catch (error) {
      console.error('[Particle Hook] Failed to create worker:', error);
    }
  }, [onFrameUpdate, onPerformanceModeChange]);

  // Initialize particle system
  const initializeParticleSystem = useCallback((config: ParticleWorkerConfig) => {
    if (!workerRef.current || !isWorkerReady) return;

    // Merge with default config
    const defaultConfig: ParticleWorkerConfig = {
      particleCount: currentPerformanceMode === 'high' ? 20 : currentPerformanceMode === 'medium' ? 10 : 5,
      maxFrameRate: 60,
      enablePhysics: true,
      gravity: 0,
      windForce: 0.1,
      boundaryBehavior: 'wrap',
      colors: ['#00ff41', '#0066ff', '#00d4ff'],
      particleSize: { min: 0.5, max: 2 },
      velocity: { min: 0.2, max: 0.8 },
      lifetime: { min: 60, max: 120 },
      canvas: {
        width: window.innerWidth,
        height: window.innerHeight,
        devicePixelRatio: Math.min(window.devicePixelRatio || 1, 2)
      }
    };

    const finalConfig = { ...defaultConfig, ...config };
    configRef.current = finalConfig;

    workerRef.current.postMessage({
      type: 'INIT',
      data: finalConfig
    });

    console.log('[Particle Hook] Particle system initialized');
  }, [isWorkerReady, currentPerformanceMode]);

  // Start animation
  const startAnimation = useCallback(() => {
    if (!workerRef.current || !isWorkerReady) return;

    workerRef.current.postMessage({
      type: 'START'
    });

    setIsRunning(true);
    console.log('[Particle Hook] Animation started');
  }, [isWorkerReady]);

  // Stop animation
  const stopAnimation = useCallback(() => {
    if (!workerRef.current) return;

    workerRef.current.postMessage({
      type: 'STOP'
    });

    setIsRunning(false);
    console.log('[Particle Hook] Animation stopped');
  }, []);

  // Update configuration
  const updateConfig = useCallback((newConfig: Partial<ParticleWorkerConfig>) => {
    if (!workerRef.current || !isWorkerReady) return;

    const updatedConfig = { ...configRef.current, ...newConfig };
    configRef.current = updatedConfig;

    workerRef.current.postMessage({
      type: 'UPDATE_CONFIG',
      data: updatedConfig
    });

    console.log('[Particle Hook] Configuration updated');
  }, [isWorkerReady]);

  // Add particle at specific location
  const addParticleAt = useCallback((x: number, y: number, config?: Partial<ParticleWorkerConfig>) => {
    if (!workerRef.current || !isWorkerReady) return;

    workerRef.current.postMessage({
      type: 'ADD_PARTICLE',
      data: { x, y, config }
    });
  }, [isWorkerReady]);

  // Clear all particles
  const clearParticles = useCallback(() => {
    if (!workerRef.current || !isWorkerReady) return;

    workerRef.current.postMessage({
      type: 'CLEAR_PARTICLES'
    });
  }, [isWorkerReady]);

  // Set performance mode
  const setPerformanceMode = useCallback((mode: 'high' | 'medium' | 'low') => {
    if (!workerRef.current || !isWorkerReady) return;

    workerRef.current.postMessage({
      type: 'SET_PERFORMANCE_MODE',
      data: { mode }
    });

    setCurrentPerformanceMode(mode);
  }, [isWorkerReady]);

  // Get performance metrics
  const getPerformanceMetrics = useCallback(() => {
    if (!workerRef.current || !isWorkerReady) return;

    workerRef.current.postMessage({
      type: 'GET_PERFORMANCE_METRICS'
    });
  }, [isWorkerReady]);

  // Auto-start if enabled
  useEffect(() => {
    if (autoStart && isWorkerReady && !isRunning) {
      // Initialize with default config first
      initializeParticleSystem({});
      
      // Start animation after a small delay
      setTimeout(() => {
        startAnimation();
      }, 100);
    }
  }, [autoStart, isWorkerReady, isRunning, initializeParticleSystem, startAnimation]);

  // Performance monitoring
  useEffect(() => {
    if (!enablePerformanceMonitoring || !isWorkerReady) return;

    const interval = setInterval(() => {
      getPerformanceMetrics();
    }, 2000); // Check every 2 seconds

    return () => clearInterval(interval);
  }, [enablePerformanceMonitoring, isWorkerReady, getPerformanceMetrics]);

  // Handle window resize
  useEffect(() => {
    if (!isWorkerReady) return;

    const handleResize = () => {
      updateConfig({
        canvas: {
          width: window.innerWidth,
          height: window.innerHeight,
          devicePixelRatio: Math.min(window.devicePixelRatio || 1, 2)
        }
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isWorkerReady, updateConfig]);

  return {
    // State
    isWorkerReady,
    isRunning,
    currentPerformanceMode,
    performanceMetrics,
    
    // Actions
    initializeParticleSystem,
    startAnimation,
    stopAnimation,
    updateConfig,
    addParticleAt,
    clearParticles,
    setPerformanceMode,
    getPerformanceMetrics,
    
    // Utils
    isSupported: () => typeof Worker !== 'undefined'
  };
}; 